import axios from "axios";
import config from "../../../config";
import { NO_DATA } from "../../lib/format";

// Wallet data from the real backend. Amounts are USD, as the wallet is kept.

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
export const apiGet = (path) => axios.get(`${config.API_BASE_URL}${path}`, auth()).then((r) => r.data).catch(() => null);
export const apiPost = (path, body) => axios.post(`${config.API_BASE_URL}${path}`, body, auth()).then((r) => r.data);

/* ---------- Formatting (design: state/wallet.tsx) ---------- */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const monthName = (m) => MONTHS[m];
const num = (n) => n !== null && n !== undefined && Number.isFinite(Number(n));
export const money = (n) => (num(n) ? `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : NO_DATA);
export const money4 = (n) => (num(n) ? `$${Math.abs(n).toFixed(4)}` : NO_DATA);
export const signed = (n) => `${n < 0 ? "−" : "+"}${Math.abs(n) >= 1 ? money(n) : money4(n)}`;
export const count = (n) => (num(n) ? Number(n).toLocaleString("en-IN") : NO_DATA);
export const shortDate = (ts) => {
  const d = new Date(ts);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
export const dateTime = (ts) => {
  const d = new Date(ts);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${shortDate(ts)}, ${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
};

/* ---------- Transactions: backend rows → what the rows show ---------- */

/** Which AI-agent charge a deduction is, from the description the billing service writes. */
function aiKind(description) {
  const d = (description || "").toLowerCase();
  if (d.includes("rental")) return "rental";
  if (d.includes("ivr") || d.includes("call")) return /inbound|incoming/.test(d) ? "call-in" : /outbound|outgoing/.test(d) ? "call-out" : "call";
  if (d.includes("web")) return "web";
  if (d.includes("whatsapp")) return "whatsapp";
  return "ai";
}

const AI_TITLE = {
  whatsapp: "AI chat on WhatsApp",
  web: "AI chat on web widget",
  "call-in": "Incoming AI call",
  "call-out": "Outgoing AI call",
  call: "AI voice call",
  rental: "Phone number rental",
  ai: "AI agent",
};

/**
 * One row of GET /api/waba/wallet/transactions as the design's Txn:
 * { id, ts, type, amount (signed), balance, kind, title, detail }.
 */
export function toTxn(r) {
  const type = r.transaction_type;
  const abs = Math.abs(Number(r.amount) || 0);
  const diff = Number(r.balance_after) - Number(r.balance_before);
  const credit = type === "deduction" ? false : type === "adjustment" ? diff >= 0 : true;
  const amount = credit ? abs : -abs;
  const description = (r.description || "").trim();
  const by = r.created_by_name || "";
  let kind = credit ? "credit" : "message";
  let title = description || "Deduction";
  let detail = description;

  if (type === "recharge") {
    const gst = Number(r.gst_amount) || 0;
    title = "Wallet recharge";
    detail = r.payment_gateway
      ? `${gst > 0 ? `${money(abs + gst)} paid, incl. ${money(gst)} GST` : `${money(abs)} paid`}${by ? ` · ${by}` : ""}`
      : by ? `Added by ${by}` : description;
  } else if (type === "refund") {
    kind = "refund";
    title = "Refund";
    detail = description.replace(/^Refund:\s*/i, "");
  } else if (type === "bonus") {
    title = "Bonus credit";
  } else if (type === "adjustment") {
    title = "Balance adjustment";
    detail = [description, by].filter(Boolean).join(" · ");
  } else if (r.channel && r.channel !== "message") {
    // Structured fields from the backend (item 129); the description is the fallback below.
    const dir = r.direction === "inbound" ? "call-in" : r.direction === "outbound" ? "call-out" : "call";
    kind = r.channel === "ai_whatsapp" ? "whatsapp" : r.channel === "ai_web" ? "web" : r.channel === "rental" ? "rental" : r.channel === "ai_voice" ? dir : aiKind(description);
    title = AI_TITLE[kind] || title;
    const mins = r.duration_seconds ? `${Math.floor(r.duration_seconds / 60)}m ${r.duration_seconds % 60}s` : "";
    detail = [r.agent_name, mins, r.recipient].filter(Boolean).join(" · ") || detail;
  } else if (r.channel === "message" || r.reference_type === "message") {
    const tpl = description.match(/template "([^"]+)"/);
    const to = description.match(/→\s*(.+)$/);
    title = r.template_name || (tpl ? tpl[1] : title);
    const cat = r.category ? r.category.charAt(0).toUpperCase() + r.category.slice(1) : "";
    detail = [cat, r.recipient ? `To ${r.recipient}` : to ? `To ${to[1]}` : "WhatsApp message"].filter(Boolean).join(" · ");
  } else if (r.reference_type === "ai_agent") {
    kind = aiKind(description);
    title = AI_TITLE[kind];
  }

  return {
    id: r.id,
    ts: new Date(r.created_at).getTime(),
    type,
    credit,
    amount,
    balance: Number(r.balance_after),
    kind,
    title,
    detail: detail || "",
  };
}
